// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";
import { ChangeEventHandler } from "react";
import styled from "styled-components";

import {
  TRUSTEE_FORM_QUESTION_ORDER,
  TrusteeCriterionGroup,
  TrusteeFormSchema,
  UsTnReclassification2026DraftData,
} from "~datatypes";

import { useFeatureVariants } from "../../../../../components/StoreProvider";
import { KeysMatching } from "../../../../../utils/typeUtils";
import { JusticeInvolvedPerson } from "../../../../../WorkflowsStore";
import { UsTnReclassification2026Form } from "../../../../../WorkflowsStore/Opportunity/Forms/UsTnReclassification2026Form";
import { FileGeneratorArgs } from "../../../DOCXFormGenerator";
import DOCXFormTextArea from "../../../DOCXFormTextArea";
import { useOpportunityFormContext } from "../../../OpportunityFormContext";
import { PrintablePage } from "../../../styles";
import FormInput from "../../CustodyReclassification/FormInput";
import { Bold, Header, TrusteeFormPage, TrusteeReworkFormPage } from "./styles";
import trusteeAssessmentTemplate from "./trustee_assessment_template.docx";
// Separate file so the pilot's template is untouched. Still a byte-for-byte
// copy awaiting authoring; see docx-template-spec.md.
import trusteeAssessmentReworkTemplate from "./trustee_assessment_template_rework.docx";
import { TrusteeApprovals } from "./TrusteeApprovals";
import {
  CriteriaSection,
  TRUSTEE_SECTIONS,
  TrusteeAssessmentHeader,
} from "./TrusteeCriteriaSection";
import { TrusteeDenialReasons, TrusteeNotesForWarden } from "./TrusteeNotes";
import { TrusteeEligibility } from "./TrusteeOutcomes";
import { getTrusteeReworkTemplateContents } from "./trusteeTemplateArgs";

type TrusteeForm = UsTnReclassification2026Form;

const CriteriaTable = styled.table`
  border-collapse: collapse;
  border: 1px solid black;

  & th,
  td {
    border: 1px solid black;
    padding: 0.25rem;

    &:last-child,
    &:nth-last-child(2) {
      width: 8%;
      text-align: center;
    }
  }

  & tbody th {
    font-weight: 500;
  }
`;

export function getTrusteeTemplateArgs(
  resident: JusticeInvolvedPerson,
  form: TrusteeForm,
  trusteeChecklistReworkEnabled = false,
): FileGeneratorArgs {
  const { derivedData, formData, formTemplateData } = form;

  if (trusteeChecklistReworkEnabled) {
    return [
      `${resident.displayName} - Trustee Checklist.docx`,
      trusteeAssessmentReworkTemplate,
      { ...formTemplateData, ...getTrusteeReworkTemplateContents(formData) },
    ];
  }

  const formContents: Record<string, string> = {};

  for (let i = 0; i < TRUSTEE_FORM_QUESTION_ORDER.length; i++) {
    const index = i + 1;
    const value = formData[TRUSTEE_FORM_QUESTION_ORDER[i]];
    formContents[`q${index}t`] = value === "true" ? "X" : "";
    formContents[`q${index}f`] = value === "false" ? "X" : "";
  }

  const { trusteeEligible } = derivedData;

  formContents.eligibleYes = trusteeEligible ? "X" : "_";
  formContents.eligibleNo = !trusteeEligible ? "X" : "_";

  formContents.approvedYes = "_";
  formContents.approvedNo = "_";

  if (trusteeEligible) {
    switch (formData.trusteeCustodyApproved) {
      case "true":
        formContents.approvedYes = "X";
        break;
      case "false":
        formContents.approvedNo = "X";
    }
  }

  formContents.denialReasons =
    formData.trusteeDenialReasons ?? "_________________________";

  formContents.notesForWarden =
    formData.trusteeNotesForWarden ?? "_________________________";

  return [
    `${resident.displayName} - Trustee Checklist.docx`,
    trusteeAssessmentTemplate,
    { ...formTemplateData, ...formContents },
  ];
}

const TrusteeCriteriaRow = observer(function TrusteeCriteriaRow({
  dataKey,
  children,
}: {
  dataKey: (typeof TRUSTEE_FORM_QUESTION_ORDER)[number];
  children: React.ReactNode;
}) {
  const opportunityForm = useOpportunityFormContext();

  const selected = opportunityForm.formData[dataKey] ?? "";
  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    opportunityForm.updateDraftData(dataKey, event.target.value);
  };

  const factorNumber = TRUSTEE_FORM_QUESTION_ORDER.indexOf(dataKey) + 1;

  return (
    <tr>
      <th scope="row" style={{ minWidth: "2.7rem" }}>
        <Bold>{factorNumber}</Bold>
      </th>
      <td>{children}</td>
      <td>
        <input
          type="radio"
          checked={selected === "true"}
          value="true"
          onChange={onChange}
        />
      </td>
      <td>
        <input
          type="radio"
          checked={selected === "false"}
          value="false"
          onChange={onChange}
        />
      </td>
    </tr>
  );
});

const Approval = observer(function Approval() {
  const opportunityForm = useOpportunityFormContext() as TrusteeForm;
  const {
    formData: { trusteeCustodyApproved },
    derivedData: { trusteeEligible },
  } = opportunityForm;

  const onClick = (value: string) => {
    if (value === trusteeCustodyApproved) {
      opportunityForm.clearDraftData("trusteeCustodyApproved");
    } else {
      opportunityForm.updateDraftData("trusteeCustodyApproved", value);
    }
  };

  const disabled = !trusteeEligible;

  return (
    <div>
      <Bold>
        Trustee Custody is
        <br />
        <input
          type="radio"
          checked={trusteeEligible && trusteeCustodyApproved === "true"}
          value="true"
          onClick={() => onClick("true")}
          disabled={disabled}
        />{" "}
        Approved
        <br />
        <input
          type="radio"
          checked={trusteeEligible && trusteeCustodyApproved === "false"}
          value="false"
          onClick={() => onClick("false")}
          disabled={disabled}
        />{" "}
        Denied
      </Bold>
    </div>
  );
});

const SignaturesReceivedContainer = styled.div`
  font-weight: 600;

  & input {
    background-color: aliceblue;
  }

  & label {
    margin-bottom: unset;
  }
`;

const TogglableRadioButton = observer(function TogglableRadioButton({
  name,
  value,
}: {
  name: KeysMatching<TrusteeFormSchema, boolean>;
  value: boolean | undefined;
}) {
  const opportunityForm = useOpportunityFormContext() as TrusteeForm;
  const { formData } = opportunityForm;
  const selected = formData[name] === value;

  // eslint-disable-next-line no-nested-ternary
  const text = value === undefined ? "Not Applicable" : value ? "Yes" : "No";

  const onClick = () => {
    if (selected || value === undefined) {
      opportunityForm.clearDraftData(name);
    } else {
      opportunityForm.updateDraftData(name, value);
    }
  };

  return (
    <label>
      {text} <input type="radio" checked={selected} onClick={onClick} />
    </label>
  );
});

function SignatureRow({
  title,
  radioName,
  dateName,
  allowNA = true,
}: {
  title: string;
  radioName: KeysMatching<TrusteeFormSchema, boolean>;
  dateName: KeysMatching<TrusteeFormSchema, string>;
  allowNA?: boolean;
}) {
  return (
    <li>
      {title}
      <ul>
        <li>
          <TogglableRadioButton name={radioName} value={true} />{" "}
          <TogglableRadioButton name={radioName} value={false} />
          {allowNA && (
            <TogglableRadioButton name={radioName} value={undefined} />
          )}
        </li>
        <li>
          Date Received{" "}
          <FormInput name={dateName} placeholder="Approval date" isDate />
        </li>
      </ul>
    </li>
  );
}

function SignaturesReceived() {
  return (
    <SignaturesReceivedContainer>
      Signatures
      <ul>
        <SignatureRow
          title="Warden"
          radioName="trusteeWardenSignature"
          dateName="trusteeWardenSignatureDate"
          allowNA={false}
        />
        <SignatureRow
          title="Contract Monitor (private facilities)"
          radioName="trusteeCMSignature"
          dateName="trusteeCMSignatureDate"
        />
        <SignatureRow
          title="Assistant Commissioner/Designee"
          radioName="trusteeACSignature"
          dateName="trusteeACSignatureDate"
        />
      </ul>
    </SignaturesReceivedContainer>
  );
}

/** The twelve-criterion form the 2026 pilot runs today; selected when the variant is off. */
const LegacyTrusteeChecklist = observer(function LegacyTrusteeChecklist({
  display,
}: {
  display: boolean;
}) {
  const opportunityForm =
    useOpportunityFormContext() as UsTnReclassification2026Form;

  const { trusteeEligible } = opportunityForm.derivedData;
  return (
    <>
      <PrintablePage stretchable hidden={!display}>
        <TrusteeFormPage>
          <Header>
            TENNESSEE CLASSIFICATION INSTRUMENT: TRUSTEE ASSESSMENT
          </Header>
          <div>
            <p>
              All inmates that have a custody score placing them in low custody,
              OR a recommended override to low custody, must be assessed for
              possible placement on trustee status using the Trustee Assessment.
            </p>

            <p>
              <Bold>
                All criteria listed on the Trustee Assessment must be marked
                TRUE for placement on trustee custody to be approved.
              </Bold>{" "}
              Use the assessment form below to determine whether a low custody
              inmate may be placed on trustee custody.{" "}
              <Bold>
                All criteria listed must be true for placement on trustee
                custody. Trustee custody inmates should be placed in an annex.
              </Bold>
            </p>

            <p>
              <Bold>NOTE:</Bold> Warden approval is required for all trustee
              custody placements. In private facilities, contract monitor
              approval is also required. Additionally, approval from the
              Assistant Commissioner of Prison Operations or their designee is
              required for trustee custody placement of an inmate that (1) has a
              disciplinary conviction for certain assaultive behavior resulting
              in serious injury or death, committed more than five years ago, OR
              (2) scores as high risk of violence on the risk assessment.
            </p>
          </div>
          <CriteriaTable>
            <thead>
              <tr>
                <th scope="col">Factor</th>
                <th scope="col">
                  CRITERIA – All must be true for placement on TRUSTEE custody
                </th>
                <th scope="col">True</th>
                <th scope="col">False</th>
              </tr>
            </thead>
            <tbody>
              <TrusteeCriteriaRow dataKey="trusteeHas10YearsOrLessRemaining">
                Time to release requirement is met: Inmate has 10 years or less
                remaining on their sentence
                <br />
                <br />
                NOTE: This automatically excludes inmates serving life or death
                sentences.
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNotConvictedOfViolentOffenseOr12MonthsInCustody">
                <Bold>If</Bold> inmate's current offense is a violent/assaultive
                offense (see Classification User’s Guide, Appendix I), the
                inmate has been in TDOC custody for a <Bold>minimum</Bold> of 12
                months
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNotServingForSexualOffense">
                Inmate is not a sex offender
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNoFelonyDetainers">
                Inmate has no felony or misdemeanor detainers and/or active
                warrants
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNoPendingFelonyCharges">
                Inmate has no pending felony charges
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNoPendingImmigrationActions">
                Inmate has no pending immigration deportation actions
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNoAssaultiveDisciplinaryWithSeriousInjury">
                Inmate has no disciplinary convictions for assaultive conduct
                that resulted in serious injury or the death of another
                individual within the past 5 years of incarceration,{" "}
                <Bold>AND</Bold>
                <br />
                <br />
                <Bold>If</Bold> the inmate has disciplinary convictions for
                assaultive conduct that resulted in serious injury or the death
                of another individual <Bold>more than</Bold> 5 years ago, the
                Assistant Commissioner for Prison Operations or their designee
                has approved trustee custody placement (signature required
                below)
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNoViolentFelonyConvictionPast5YearsIncarceration">
                Inmate has no court-prosecuted felony convictions for a
                violent/assaultive offense convicted during the past 5 years of
                incarceration
              </TrusteeCriteriaRow>
            </tbody>
          </CriteriaTable>
        </TrusteeFormPage>
      </PrintablePage>
      <PrintablePage stretchable hidden={!display}>
        <TrusteeFormPage>
          <CriteriaTable>
            <tbody>
              <TrusteeCriteriaRow dataKey="trusteeNoEscapeFromMediumCloseMaxPast10Years">
                Inmate has no escape or attempted escape from medium, close, or
                maximum custody within the last 10 years of incarceration.
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNoEscapeFromLowTrusteePast5Years">
                Inmate has no escape on record from low or trustee custody
                within the last 5 years of incarceration. “Low custody” includes
                prior placements at minimum direct, minimum restrict, or minimum
                trustee custody levels.
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeNotScoredHighForViolence">
                Inmate has not scored high for violence on the risk assessment,{" "}
                <Bold>OR</Bold>
                <br />
                <br />
                <Bold>If</Bold> inmate has scored high for violence on the risk
                assessment, the Assistant Commissioner for Prison Operations or
                their designee has approved trustee custody placement (signature
                required below)
              </TrusteeCriteriaRow>

              <TrusteeCriteriaRow dataKey="trusteeWardenHasApproved">
                Warden has approved trustee custody placement (signature
                required below)
                <br />
                <br />
                NOTE: In private facilities, Contract Monitor must{" "}
                <Bold>also</Bold> approve trustee custody placement (signature
                required below).
              </TrusteeCriteriaRow>
            </tbody>
          </CriteriaTable>
          <div>
            <Bold>
              TRUSTEE CUSTODY ELIGIBLE YES
              <input type="radio" checked={trusteeEligible} disabled /> NO
              <input type="radio" checked={!trusteeEligible} disabled />
            </Bold>
          </div>
          <Approval />
          <div>
            <Bold>Reasons for Denial:</Bold>
            <DOCXFormTextArea<UsTnReclassification2026DraftData> name="trusteeDenialReasons" />
          </div>
          <div>
            <Bold>Notes for Warden Review:</Bold>
            <DOCXFormTextArea<UsTnReclassification2026DraftData> name="trusteeNotesForWarden" />
          </div>
          <SignaturesReceived />
        </TrusteeFormPage>
      </PrintablePage>
    </>
  );
});

/**
 * Where the page breaks fall; a group never splits across a page. Sized against
 * a measured render, so re-check a print preview after changing TRUSTEE_CRITERIA.
 */
export const TRUSTEE_PRINT_BLOCKS = [
  "eligibility",
  "approvals",
  "notes",
] as const;

export type TrusteePrintBlock = (typeof TRUSTEE_PRINT_BLOCKS)[number];

export const TRUSTEE_PRINT_PAGES = [
  { groups: ["A"], blocks: [] },
  { groups: ["B", "C", "D"], blocks: [] },
  // Eligibility sits under the last group rather than on a sheet of its own.
  // Measured at 671 against 698 with every hard bar failed.
  { groups: ["E"], blocks: ["eligibility"] },
  // Approvals starts a sheet, so the table and what follows it stay together.
  { groups: [], blocks: ["approvals", "notes"] },
] as const satisfies readonly {
  groups: readonly TrusteeCriterionGroup[];
  blocks: readonly TrusteePrintBlock[];
}[];

/**
 * The same form on three sheets, which is what the downloaded DOCX uses.
 *
 * A Word page carries about 9in of content against the 698px this app's fixed
 * page gives, so the DOCX has room the screen does not. It can also paginate:
 * a `PrintablePage` clips whatever runs past its bottom edge, while Word pushes
 * the overflow onto another sheet. Only the eligibility block can reach that
 * size, and only when most of the hard bars failed.
 *
 * Nothing in the app reads this. It is dumped for the template generator, and
 * `TrusteePrintPages.test.ts` holds it to the same completeness rules as the
 * plan above.
 */
export const TRUSTEE_DOCX_PRINT_PAGES = [
  { groups: ["A", "B"], blocks: [] },
  { groups: ["C", "D", "E"], blocks: ["eligibility"] },
  { groups: [], blocks: ["approvals", "notes"] },
] as const satisfies readonly {
  groups: readonly TrusteeCriterionGroup[];
  blocks: readonly TrusteePrintBlock[];
}[];

const ReworkTrusteeChecklist = observer(function ReworkTrusteeChecklist({
  display,
}: {
  display: boolean;
}) {
  const announced = new Set<string>();

  return (
    <>
      {TRUSTEE_PRINT_PAGES.map(({ groups, blocks }, pageIndex) => {
        // A page may carry two sections; each gets its own table, and its
        // heading only where the section actually begins.
        const pageSections = TRUSTEE_SECTIONS.map(
          ({ section, groups: all }) => ({
            section,
            groups: all.filter((g) =>
              (groups as readonly string[]).includes(g.key),
            ),
          }),
        ).filter(({ groups: g }) => g.length > 0);

        const carries = (block: TrusteePrintBlock) =>
          (blocks as readonly string[]).includes(block);

        return (
          // Fixed height: the page count must not change as answers are entered.
          <PrintablePage
            hidden={!display}
            key={groups.join("") || blocks.join("")}
          >
            <TrusteeReworkFormPage>
              {pageIndex === 0 && <TrusteeAssessmentHeader />}
              {pageSections.map(({ section, groups: sectionGroups }) => {
                const startsSection = !announced.has(section);
                announced.add(section);

                return (
                  <CriteriaSection
                    key={section}
                    section={section}
                    groups={sectionGroups}
                    showHeading={startsSection}
                  />
                );
              })}
              {carries("eligibility") && <TrusteeEligibility />}
              {carries("approvals") && <TrusteeApprovals />}
              {carries("notes") && (
                <>
                  <TrusteeDenialReasons />
                  <TrusteeNotesForWarden />
                </>
              )}
            </TrusteeReworkFormPage>
          </PrintablePage>
        );
      })}
    </>
  );
});

export const TrusteeChecklist = observer(function TrusteeChecklist({
  display,
}: {
  display: boolean;
}) {
  const { trusteeChecklistRework } = useFeatureVariants();

  return trusteeChecklistRework ? (
    <ReworkTrusteeChecklist display={display} />
  ) : (
    <LegacyTrusteeChecklist display={display} />
  );
});
